/**
 * rosterSync.js
 *
 * Synchronises the master class-list roster into the grading session and
 * grade book after a class-list reload.  The merge is id-based so that:
 *
 *   - New students are appended with default (empty) grading records.
 *   - Existing students have their identity fields updated (name, email, …)
 *     without touching any grading data.
 *   - Students that no longer appear in the master list are flagged as
 *     "removed" but their grading data is preserved for audit purposes.
 */

// ── helpers ────────────────────────────────────────────────────────────

const IDENTITY_FIELDS = ['name', 'email', 'program', 'level', 'campus', 'status', 'component', 'studentNumber'];

/**
 * Build a Map keyed by student.id for O(1) lookups.
 */
const buildStudentMap = (students) => {
    const map = new Map();
    (students || []).forEach(s => { if (s && s.id) map.set(s.id, s); });
    return map;
};

/**
 * Create a default grading-progress entry for a brand-new student.
 */
const createDefaultProgress = () => ({
    status: 'pending',
    gradeType: null,
    lastModified: null,
});

/**
 * Update only identity (non-grade) fields on a student object.
 * Returns a new object if anything changed, or the original reference if not.
 */
const mergeIdentityFields = (existing, source) => {
    let changed = false;
    const merged = { ...existing };

    IDENTITY_FIELDS.forEach(field => {
        if (source[field] !== undefined && source[field] !== null && source[field] !== '' && source[field] !== existing[field]) {
            merged[field] = source[field];
            changed = true;
        }
    });

    return changed ? merged : existing;
};

// ── main sync function ─────────────────────────────────────────────────

/**
 * Synchronise the updated master class-list into the grading session and
 * grade book.  This is the single function that should be called after
 * `classList` has been set with the merged roster.
 *
 * @param {Object}   params
 * @param {Object[]} params.mergedStudents    - The post-reload master student list.
 * @param {Object[]} params.mergedProgress    - The post-reload grading progress array.
 * @param {Object}   params.courseMetadata     - The post-reload course metadata.
 * @param {Object}   params.gradingSession     - Current grading session state.
 * @param {Function} params.setGradingSession  - Setter for grading session.
 * @param {Object}   params.gradeBook          - Current grade book state (may be null).
 * @param {Function} params.setGradeBook       - Setter for grade book.
 */
export const syncRosterAcrossSessionAndGradebook = ({
    mergedStudents,
    mergedProgress,
    courseMetadata,
    gradingSession,
    setGradingSession,
    gradeBook,
    setGradeBook,
}) => {
    // ─── 1. Sync grading session ───────────────────────────────────────
    if (gradingSession && gradingSession.active && gradingSession.classListData) {
        const masterMap = buildStudentMap(mergedStudents);
        const sessionStudents = gradingSession.classListData.students || [];
        const sessionProgress = gradingSession.classListData.gradingProgress || [];

        // Build a set of IDs already present in the session
        const sessionIdSet = new Set(sessionStudents.map(s => s.id));

        // Start with copies of the session arrays
        const updatedStudents = [];
        const updatedProgress = [];

        // Pass 1: walk existing session students – update identity or mark removed
        sessionStudents.forEach((student, idx) => {
            const masterStudent = masterMap.get(student.id);
            if (masterStudent) {
                // Student still exists in master → update identity fields only
                updatedStudents.push(mergeIdentityFields(student, masterStudent));
            } else {
                // Student removed from master → keep but flag as removed
                updatedStudents.push({
                    ...student,
                    status: 'removed',
                    _removedAt: new Date().toISOString(),
                });
            }
            // Always preserve the existing grading progress entry
            updatedProgress.push(sessionProgress[idx] || createDefaultProgress());
        });

        // Pass 2: append students that are new (in master but not in session)
        mergedStudents.forEach((student, masterIdx) => {
            if (!sessionIdSet.has(student.id)) {
                updatedStudents.push(student);
                // Use the progress from the merged classList if available, else default
                updatedProgress.push(mergedProgress[masterIdx] || createDefaultProgress());
            }
        });

        // Ensure currentStudentIndex stays valid
        let currentStudentIndex = gradingSession.currentStudentIndex || 0;
        if (currentStudentIndex >= updatedStudents.length) {
            currentStudentIndex = Math.max(0, updatedStudents.length - 1);
        }

        // If the current student was marked removed, keep the index but let the
        // user see it — they can navigate away.  No forced jump.
        const currentStudent = updatedStudents[currentStudentIndex] || null;

        setGradingSession(prev => ({
            ...prev,
            classListData: {
                ...prev.classListData,
                students: updatedStudents,
                gradingProgress: updatedProgress,
                courseMetadata: {
                    ...(prev.classListData.courseMetadata || {}),
                    ...(courseMetadata || {}),
                    totalStudents: updatedStudents.length,
                },
            },
            currentStudentIndex,
            currentStudent,
        }));

        console.log('✅ Grading session synced with reload:', {
            previousCount: sessionStudents.length,
            newCount: updatedStudents.length,
            added: updatedStudents.length - sessionStudents.length,
        });
    }

    // ─── 2. Sync grade book ────────────────────────────────────────────
    // (The existing reload code in ClassListManager already handles gradeBook
    //  sync for new/updated students.  Here we additionally handle the
    //  "removed" case and ensure counts are consistent.)
    if (gradeBook) {
        const masterIdSet = new Set(mergedStudents.map(s => s.id));

        setGradeBook(prev => {
            if (!prev) return prev;

            const gbStudents = (prev.students || []).map(s => {
                if (!masterIdSet.has(s.id)) {
                    // Student removed from master list
                    return { ...s, status: 'removed', _removedAt: new Date().toISOString() };
                }
                return s;
            });

            return {
                ...prev,
                students: gbStudents,
                metadata: {
                    ...prev.metadata,
                    lastModified: new Date().toISOString(),
                },
            };
        });
    }
};

// ── Exported for testing ────────────────────────────────────────────────

export { buildStudentMap, createDefaultProgress, mergeIdentityFields, IDENTITY_FIELDS };
